import { Directive, booleanAttribute, effect, inject, input } from '@angular/core';
import { ValueAccessorProviderFactory } from '../value-accessor/value-accessor-provider-factory';
import { PhonePipe } from '../../pipes/phone.pipe';
import { BaseNumberInputDirective } from './base-number-input.directive';
import {
  applyDigitPaste,
  canInsertDigit,
  isKeyboardNavigationOrEditKey,
  isPrintableDigitKey
} from './input-parse-format.helpers';

import type { ControlValueAccessor } from '@angular/forms';

/**
 * Phone Input Directive
 *
 * Allows an input type number to be formatted on blur
 *
 * @example
 * <input type="number" tdWbPhoneInput>
 */
@Directive({
  selector: 'input[tdWbPhoneInput]',
  providers: [ValueAccessorProviderFactory(PhoneInputDirective)],
  host: {
    '(input)': 'onInput($event)',
    '(keydown)': 'onKeyDown($event)',
    '(paste)': 'onPaste($event)',
    '(focus)': 'onFocus()',
    '(blur)': 'onBlur()'
  }
})
export class PhoneInputDirective extends BaseNumberInputDirective implements ControlValueAccessor {
  protected readonly phonePipe = inject<PhonePipe>(PhonePipe);

  private readonly DEFAULT_LENGTH: number = 10;
  private readonly COUNTRY_CODE_LENGTH: number = 11;
  readonly countryCode = input<boolean, unknown>(false, {
    alias: 'tdWbPhoneInput',
    transform: booleanAttribute
  });

  constructor() {
    super('true');

    effect((): void => {
      this.countryCode();
      super.onChanges();
    });
  }

  // Prevent additive input beyond max length, but allow deletion/navigation.
  // Block non-digit printable keys (including '.' and ',').

  onKeyDown(event: KeyboardEvent): void {
    const el = this.elementRef.nativeElement as HTMLInputElement;
    const key = event.key;

    // Allow control/meta shortcuts and navigation/deletion keys
    if (event.ctrlKey || event.metaKey || event.altKey) { return; }
    if (isKeyboardNavigationOrEditKey(key)) {
      return; // never block these
    }

    // Only handle single-character (printable) keys from here
    if (key.length !== 1) { return; }

    // Phone input accepts digits only; explicitly block '.' and ',' and any other non-digit
    if (!isPrintableDigitKey(key)) {
      event.preventDefault();
      return;
    }

    const value = el.value ?? '';
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    if (!canInsertDigit(value, { start, end }, this.maxLength())) {
      // At capacity with no selection: block additive input
      event.preventDefault();
    }
  }

  onPaste(event: ClipboardEvent): void {
    // Stop data actually being pasted into input
    // Also stopImmediatePropagation to prevent the base directive's paste handler
    // from running on the same element and double-processing the value.
    event.stopImmediatePropagation?.();
    event.stopPropagation();
    event.preventDefault();

    const input = event.target as HTMLInputElement;
    const before = input.value ?? '';
    const start = input.selectionStart ?? before.length;
    const end = input.selectionEnd ?? before.length;

    const next = applyDigitPaste({
      currentValue: before,
      selection: { start, end },
      pastedText: event.clipboardData?.getData('text') ?? '',
      maxLength: this.maxLength()
    });
    this.renderer.setProperty(input, 'value', next);

    // Trigger base processing by simulating a minimal KeyboardEvent shape
    const fakeEvent = { target: input, key: '' } as unknown as KeyboardEvent;
    super.onKeyUp(fakeEvent);
  }

  writeValue(value: unknown): void {
    let formattedValue = '';

    if (!this.isEmpty(value)) {
      if (!this.focused) {
        formattedValue = this.formatValue(value as number);
      } else {
        formattedValue = value.toString();
      }
    }
    if ((this.viewValue !== formattedValue || this.viewValue === undefined)) {
      this.renderer.setProperty(this.elementRef.nativeElement, 'value', formattedValue);
      this.viewValue = formattedValue;
    }
    this.value = value as number;
  }

  protected formatValue(value: number): string {
    return this.phonePipe.transform(value?.toString());
  }

  protected parseValue(value: string): number {
    return Number(value);
  }

  private maxLength(): number {
    return this.countryCode() ? this.COUNTRY_CODE_LENGTH : this.DEFAULT_LENGTH;
  }
}
